import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { FormLayout } from '../components/FormLayout';
import { MarkdownEditor } from '../components/MarkdownEditor';
import type { Car, ForumCategory, ForumThread, SingleResponse } from '../api/types';

interface ListResponse<T> {
  data: T[];
}

export function NewForumThread() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const carIdParam = searchParams.get('carId');
  const carId = carIdParam ? Number(carIdParam) : null;

  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [car, setCar] = useState<Car | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiFetch<ListResponse<ForumCategory>>('/api/v1/forum/categories').then((res) => {
      setCategories(res.data);
      if (res.data[0]) setCategoryId(res.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!carId) return;
    apiFetch<SingleResponse<Car>>(`/api/v1/cars/${carId}`)
      .then((res) => setCar(res.data))
      .catch(() => setCar(null));
  }, [carId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await apiFetch<SingleResponse<ForumThread>>('/api/v1/forum/threads', {
        method: 'POST',
        body: JSON.stringify({
          forum_category_id: categoryId,
          car_id: carId,
          title,
          body,
        }),
      });
      const created = res.data;
      navigate(`/forum/threads/${created.slug}-${created.id}`);
    } catch (err: any) {
      setError(err.message ?? t('newForumThread.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <FormLayout
        header={<PageHeader />}
        title={t('newForumThread.title')}
        error={error || null}
        columns={{ form: 5, aside: 7 }}
        form={
          <>
            {car && (
              <Chip
                icon={<DirectionsCarIcon />}
                label={t('newForumThread.aboutCar', { nickname: car.display_name })}
                color="primary"
                variant="outlined"
                sx={{ alignSelf: 'flex-start' }}
              />
            )}

            <TextField
              select
              label={t('newForumThread.category')}
              value={categoryId}
              onChange={(e) =>
                setCategoryId(typeof e.target.value === 'number' ? e.target.value : Number(e.target.value))
              }
              required
              fullWidth
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={t('newForumThread.threadTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
            />
          </>
        }
        aside={
          <MarkdownEditor
            label={t('newForumThread.body')}
            value={body}
            onChange={setBody}
            required
            rows={14}
          />
        }
        footer={
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" size="large" disabled={submitting}>
              {submitting ? t('common.creating') : t('common.submit')}
            </Button>
          </Stack>
        }
      />
    </Box>
  );
}
